/**
 * Hook for fetching wash sale restrictions data.
 * Uses SWR for caching, deduplication, and automatic revalidation.
 */

import useSWR from 'swr';
import { fetchWashSales } from '@/services/api';
import type { WashSaleRestriction } from '@/types';

interface UseWashSalesReturn {
  restrictions: WashSaleRestriction[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Fetch active wash sale restrictions for a specific account.
 *
 * @param accountId - The account ID to fetch restrictions for (null to skip fetch)
 * @returns Restrictions data, loading state, error, and refresh function
 */
export function useWashSales(accountId: string | null): UseWashSalesReturn {
  const { data, error, isLoading, mutate } = useSWR(
    accountId ? ['wash-sales', accountId] : null,
    () => (accountId ? fetchWashSales(accountId) : null),
    {
      refreshInterval: 60000,
      revalidateOnFocus: true,
      dedupingInterval: 5000,
    }
  );

  return {
    restrictions: data?.restrictions ?? [],
    isLoading,
    error: error?.message ?? null,
    refresh: () => mutate(),
  };
}
